import { useMemo } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";

import { BulletinSchema, cachedBulletinSchema } from "./schemas";

/**
 * The schema for one bulletin type, for readers that already hold the document — the page, which
 * derives the issue list and the rail ticks from it.
 */
export const useBulletinSchema = (
  bulletinType: string | null,
): BulletinSchema => {
  const { t } = useTranslation("bulletin/create-v2");
  return cachedBulletinSchema(bulletinType, t);
};

/**
 * The same schema as a react-hook-form resolver, for `BulletinFormProvider`.
 *
 * **The resolver picks its schema from the values it is handed**, rather than being rebuilt when
 * the bulletin type changes. The README's phase 3 plan said
 * `resolver: zodResolver(useBulletinSchema(bulletinType))`, which cannot work as written: the
 * bulletin type is a *form value*, and the provider is the component that creates the form, so it
 * has nothing to read it from before `useForm` exists. Watching it there would also make the
 * resolver identity change mid-life, which react-hook-form is not documented to pick up.
 *
 * Reading it off `values` costs nothing — `cachedBulletinSchema` memoises — and the resolver stays
 * one stable function for the life of the form, which is what `useForm` wants.
 */
export const useBulletinResolver = (): Resolver<BulletinFormValues> => {
  const { t } = useTranslation("bulletin/create-v2");

  return useMemo<Resolver<BulletinFormValues>>(
    () => (values, context, options) =>
      zodResolver(cachedBulletinSchema(values.bulletinType, t))(
        values,
        context,
        options,
      ),
    [t],
  );
};
